import { Component, OnInit, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { DocumentService } from '../../../../../../services/document/document.service';
import { PaiementService } from '../../../../../../services/paiement/paiement.service';
import { forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

/** Statut "Non utilisé" = avoir disponible ; "Partiellement utilisé" = affiché aussi dans Depuis un avoir */
const STATUT_NON_UTILISE_ID = 19;
const STATUT_PARTIELLEMENT_UTILISE_ID = 20;
const TYPE_FACTURE_ID = 11;
const TYPE_FACTURE_FOURNISSEUR_ID = 14;

@Component({
  selector: 'app-depuis-avoir',
  templateUrl: './depuis-avoir.component.html',
  styleUrls: ['./depuis-avoir.component.scss']
})
export class DepuisAvoirComponent implements OnInit, OnChanges {

  @Input() facture: any = null;
  @Input() documentType: string | null = null; // 'facture' | 'facture-fournisseur'
  @Input() stationId: number | null = null;
  /** Incrémenté par le parent après création d'un paiement pour recharger la liste et MONTANT RESTANT */
  @Input() refreshTrigger: number = 0;
  @Output() utiliserAvoirClick = new EventEmitter<any>();
  /** Émis pour créer directement le paiement avec le montant restant (sans passer par le formulaire). */
  @Output() ajouterPaiementAvoirClick = new EventEmitter<{ avoir: any; montantRestant: string }>();

  avoirs: any[] = [];
  /** total_ttc - somme des montants des lignes_paiement pour chaque avoir (document_id = avoir.id) */
  totalPayeParAvoirId = new Map<number, number>();
  loading = false;
  error: string | null = null;

  constructor(
    private documentService: DocumentService,
    private paiementService: PaiementService
  ) {}

  ngOnInit(): void {
    this.loadAvoirs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['facture'] || changes['documentType'] || changes['stationId'] || changes['refreshTrigger']) {
      this.loadAvoirs();
    }
  }

  loadAvoirs(): void {
    if (!this.facture || !this.documentType) {
      this.avoirs = [];
      return;
    }
    const typeDocumentId = this.documentType === 'facture-fournisseur' ? TYPE_FACTURE_FOURNISSEUR_ID : TYPE_FACTURE_ID;
    this.error = null;
    this.loading = true;
    const documentId = this.getDocumentIdFromDoc(this.facture);
    if (documentId == null) {
      this.avoirs = [];
      this.error = 'Document sans identifiant.';
      this.loading = false;
      return;
    }

    // Charger les avoirs "Non utilisé" (19) et "Partiellement utilisé" (20) pour les afficher dans Depuis un avoir
    forkJoin({
      nonUtilise: this.documentService.getByStatutAndType(STATUT_NON_UTILISE_ID, typeDocumentId),
      partiellementUtilise: this.documentService.getByStatutAndType(STATUT_PARTIELLEMENT_UTILISE_ID, typeDocumentId)
    }).pipe(
      map(({ nonUtilise, partiellementUtilise }) => {
        const toList = (r: any) => r?.data?.documents ?? r?.data ?? (Array.isArray(r) ? r : []);
        const list1 = Array.isArray(toList(nonUtilise)) ? toList(nonUtilise) : [];
        const list2 = Array.isArray(toList(partiellementUtilise)) ? toList(partiellementUtilise) : [];
        const byId = new Map<number, any>();
        [...list1, ...list2].forEach((doc: any) => {
          const id = doc.id ?? (doc.fullData || doc)?.id;
          if (id != null && !byId.has(Number(id))) byId.set(Number(id), doc);
        });
        return Array.from(byId.values());
      })
    ).pipe(
      switchMap((docs: any[]) => {
        const filtered = docs.filter((doc: any) => {
          const d = doc.fullData || doc;
          const originalId = d.document_original_id != null ? Number(d.document_original_id) : null;
          if (originalId == null) return false;
          if (originalId !== documentId) return false;
          if (this.stationId != null && doc.station_id != null && doc.station_id !== this.stationId) return false;
          return true;
        });
        this.avoirs = filtered;
        const avoirIds = filtered.map((a: any) => this.getAvoirId(a)).filter((id: number | null): id is number => id != null);
        if (avoirIds.length === 0) {
          this.totalPayeParAvoirId.clear();
          return of(null);
        }
        const requests: Record<string, ReturnType<PaiementService['getPaiementsByDocumentId']>> = {};
        avoirIds.forEach((id) => { requests[String(id)] = this.paiementService.getPaiementsByDocumentId(id); });
        return forkJoin(requests).pipe(
          map((responses) => {
            avoirIds.forEach((id) => {
              const res = responses?.[String(id)];
              this.totalPayeParAvoirId.set(id, this.sumMontantsFromPaiements(res));
            });
            return null;
          })
        );
      })
    ).subscribe({
      next: () => { this.loading = false; },
      error: (err) => {
        this.loading = false;
        this.avoirs = [];
        this.totalPayeParAvoirId.clear();
        this.error = err?.error?.message || 'Erreur lors du chargement des avoirs.';
      }
    });
  }

  private getAvoirId(avoir: any): number | null {
    if (!avoir) return null;
    const d = avoir.fullData ?? avoir;
    if (d?.id != null) return Number(d.id);
    if (avoir?.id != null) return Number(avoir.id);
    return null;
  }

  /**
   * Somme des montants de toutes les lignes_paiement des paiements renvoyés par getPaiementsByDocumentId(document_id avoir).
   */
  private sumMontantsFromPaiements(response: any): number {
    if (response == null) return 0;
    let paiements: any[] = [];
    if (Array.isArray(response)) paiements = response;
    else if (response?.data?.paiements && Array.isArray(response.data.paiements)) paiements = response.data.paiements;
    else if (response?.data && Array.isArray(response.data)) paiements = response.data;
    else if (response?.data?.paiement) paiements = [response.data.paiement];
    else if (response?.paiements) paiements = response.paiements;
    let sum = 0;
    paiements.forEach((p: any) => {
      const lignes = p?.lignes_paiement ?? p?.lignesPaiement ?? [];
      (Array.isArray(lignes) ? lignes : []).forEach((l: any) => {
        const m = l?.montant;
        const n = typeof m === 'number' ? m : parseFloat(String(m || '0').replace(',', '.'));
        if (!isNaN(n)) sum += n;
      });
    });
    return sum;
  }

  private getDocumentIdFromDoc(doc: any): number | null {
    if (!doc) return null;
    const d = doc.fullData || doc;
    if (d.id != null) return Number(d.id);
    if (doc.id != null) return Number(doc.id);
    if (d.document_id != null) return Number(d.document_id);
    if (doc.document_id != null) return Number(doc.document_id);
    return null;
  }

  private getClientIdFromDoc(doc: any): number | null {
    if (!doc) return null;
    const d = doc.fullData || doc;
    if (d.client_id) return Number(d.client_id);
    if (d.client_data?.client_id) return Number(d.client_data.client_id);
    if (d.clients?.[0]?.pivot?.client_id) return Number(d.clients[0].pivot.client_id);
    if (d.clients?.[0]?.client_id) return Number(d.clients[0].client_id);
    if (d.clients?.[0]?.id) return Number(d.clients[0].id);
    if (d.client?.id) return Number(d.client.id);
    return null;
  }

  private getFournisseurIdFromDoc(doc: any): number | null {
    if (!doc) return null;
    const d = doc.fullData || doc;
    if (d.fournisseur_id) return Number(d.fournisseur_id);
    if (d.fornisseur_data?.fournisseur_id) return Number(d.fornisseur_data.fournisseur_id);
    if (d.fornisseurs?.[0]?.pivot?.fournisseur_id) return Number(d.fornisseurs[0].pivot.fournisseur_id);
    if (d.fornisseurs?.[0]?.fournisseur_id) return Number(d.fornisseurs[0].fournisseur_id);
    if (d.fornisseurs?.[0]?.id) return Number(d.fornisseurs[0].id);
    if (d.fournisseur?.id) return Number(d.fournisseur.id);
    return null;
  }

  /** Montant total de l'avoir (Total TTC) : priorité à total_ttc pour cohérence avec le document. */
  getMontant(doc: any): string {
    const d = doc?.fullData ?? doc;
    const val = d?.total_ttc ?? doc?.total_ttc ?? d?.net_payer ?? doc?.net_payer ?? d?.revenue ?? doc?.revenue ?? 0;
    const num = typeof val === 'number' ? val : parseFloat(String(val).replace(/[^\d.,\-]/g, '').replace(',', '.'));
    const abs = Math.abs(isNaN(num) ? 0 : num);
    return abs.toFixed(2);
  }

  /**
   * Montant restant = total_ttc de l'avoir − somme des montants des lignes_paiement
   * (paiements récupérés via getPaiementsByDocumentId(document_id de l'avoir)).
   */
  getMontantRestant(doc: any): string {
    const d = doc?.fullData ?? doc;
    const totalTtcRaw = d?.total_ttc ?? doc?.total_ttc ?? 0;
    const totalTtc = Math.abs(typeof totalTtcRaw === 'number' ? totalTtcRaw : parseFloat(String(totalTtcRaw).replace(/[^\d.,\-]/g, '').replace(',', '.')) || 0);

    const avoirId = this.getAvoirId(doc);
    const totalPaye = avoirId != null ? (this.totalPayeParAvoirId.get(avoirId) ?? 0) : 0;
    const restant = Math.max(0, totalTtc - totalPaye);
    return restant.toFixed(2);
  }

  /** Émet l'avoir et le montant restant pour pré-remplir le nouveau paiement avec le reste (pas le total). */
  utiliserAvoir(avoir: any): void {
    const montantRestant = this.getMontantRestant(avoir);
    this.utiliserAvoirClick.emit({ avoir, montantRestant });
  }

  /** Crée le paiement avec le montant restant : émet vers le parent qui pré-remplit le formulaire et déclenche la création. */
  ajouterPaiementAvoir(avoir: any): void {
    const montantRestant = this.getMontantRestant(avoir);
    this.ajouterPaiementAvoirClick.emit({ avoir, montantRestant });
  }
}
